import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { PermissionsService } from './permissions.service';
import { UserPermission, UserCompany, UserPermissionsData } from '../interfaces/permission.interface';

@Injectable({
  providedIn: 'root'
})
export class UserPermissionsService {
  private permissionsSubject = new BehaviorSubject<UserPermission[]>([]);
  public permissions$: Observable<UserPermission[]> = this.permissionsSubject.asObservable();

  private companiesSubject = new BehaviorSubject<UserCompany[]>([]);
  public userCompanies$: Observable<UserCompany[]> = this.companiesSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  private userCodeSubject = new BehaviorSubject<string | null>(null);
  public userCode$: Observable<string | null> = this.userCodeSubject.asObservable();

  private hasNoAccessSubject = new BehaviorSubject<boolean>(false);
  public hasNoAccess$: Observable<boolean> = this.hasNoAccessSubject.asObservable();

  constructor(private permissionsService: PermissionsService) { }

  /**
   * Decodificar token JWT para obtener información del usuario
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decodificando token:', error);
      return null;
    }
  }

  /**
   * Obtener UserCode del token almacenado en localStorage
   * Prioriza el 'sub' de Cognito que es el identificador único del usuario
   */
  getUserCodeFromToken(): string | null {
    const token = localStorage.getItem('id_token');
    if (!token) {
      return null;
    }

    const decoded = this.decodeToken(token);
    if (!decoded) {
      return null;
    }

    // Priorizar 'sub' que es el identificador único de Cognito
    // 'sub' es un UUID y es consistente independientemente del proveedor de identidad
    let userCode = decoded['sub'];

    // Si no hay 'sub', intentar con cognito:username (fallback)
    if (!userCode) {
      userCode = decoded['cognito:username'];
    }

    // Último fallback: email
    if (!userCode) {
      userCode = decoded['email'];
    }

    if (userCode) {
      this.userCodeSubject.next(userCode);
    }

    return userCode;
  }

  /**
   * Cargar permisos del usuario desde la API
   */
  loadUserPermissions(): Observable<UserPermission[]> {
    const userCode = this.getUserCodeFromToken();

    if (!userCode) {
      this.loadingSubject.next(false);
      return throwError(() => new Error('UserCode no disponible del token'));
    }

    this.loadingSubject.next(true);

    return this.permissionsService.getUserPermissions(userCode).pipe(
      map(response => {
        if (response.success && response.data) {
          // Nueva estructura: { permissions: [], companies: [] }
          if (response.data.permissions !== undefined && response.data.companies !== undefined) {
            const data = response.data as UserPermissionsData;
            return { permissions: data.permissions || [], companies: data.companies || [] };
          }
          // Fallback: respuesta legacy (solo array de permisos)
          const permissions = Array.isArray(response.data) ? response.data : [response.data];
          return { permissions, companies: [] };
        }
        return { permissions: [], companies: [] };
      }),
      tap(data => {
        // Actualizar permisos
        this.permissionsSubject.next(data.permissions);

        // Actualizar compañías autorizadas
        this.companiesSubject.next(data.companies);

        this.loadingSubject.next(false);

        // Verificar si el usuario no tiene permisos
        if (data.permissions.length === 0) {
          this.hasNoAccessSubject.next(true);
        } else {
          this.hasNoAccessSubject.next(false);
        }
      }),
      map(data => data.permissions), // Retornar solo permisos para compatibilidad
      catchError(error => {
        this.loadingSubject.next(false);
        this.permissionsSubject.next([]);
        this.companiesSubject.next([]);
        return throwError(() => new Error(`Falló al cargar permisos: ${error?.status || error?.message || 'Error desconocido'}`));
      })
    );
  }

  /**
   * Obtener permisos actuales (snapshot)
   */
  getPermissions(): UserPermission[] {
    return this.permissionsSubject.value;
  }

  /**
   * Verificar si el usuario tiene acceso a un módulo específico
   */
  canViewModule(moduleCode: string): boolean {
    const permissions = this.getPermissions();
    const modulePermission = permissions.find(p => p.Code === moduleCode);
    // Convertir 1/0 a boolean explícitamente
    return modulePermission ? (modulePermission.CanView === 1 || modulePermission.CanView === true) : false;
  }

  /**
   * Verificar si el usuario puede crear en un módulo
   */
  canCreateInModule(moduleCode: string): boolean {
    const permissions = this.getPermissions();
    const modulePermission = permissions.find(p => p.Code === moduleCode);
    return modulePermission ? (modulePermission.CanCreate === 1 || modulePermission.CanCreate === true) : false;
  }

  /**
   * Verificar si el usuario puede editar en un módulo
   */
  canEditInModule(moduleCode: string): boolean {
    const permissions = this.getPermissions();
    const modulePermission = permissions.find(p => p.Code === moduleCode);
    return modulePermission ? (modulePermission.CanEdit === 1 || modulePermission.CanEdit === true) : false;
  }

  /**
   * Verificar si el usuario puede eliminar en un módulo
   */
  canDeleteInModule(moduleCode: string): boolean {
    const permissions = this.getPermissions();
    const modulePermission = permissions.find(p => p.Code === moduleCode);
    return modulePermission ? (modulePermission.CanDelete === 1 || modulePermission.CanDelete === true) : false;
  }

  /**
   * Verificar si el usuario tiene un permiso específico en un módulo
   */
  hasPermission(moduleCode: string, action: 'view' | 'create' | 'edit' | 'delete'): boolean {
    switch (action) {
      case 'view':
        return this.canViewModule(moduleCode);
      case 'create':
        return this.canCreateInModule(moduleCode);
      case 'edit':
        return this.canEditInModule(moduleCode);
      case 'delete':
        return this.canDeleteInModule(moduleCode);
      default:
        return false;
    }
  }

  /**
   * Obtener el módulo por ruta
   */
  getModuleByRoute(route: string): UserPermission | undefined {
    const permissions = this.getPermissions();
    return permissions.find(p => p.Route === route || route.includes(p.Route || ''));
  }

  /**
   * Verificar acceso a una ruta específica
   */
  canAccessRoute(route: string): boolean {
    const module = this.getModuleByRoute(route);
    return module ? (module.CanView === 1 || module.CanView === true) : false;
  }

  /**
   * Obtener compañías autorizadas del usuario (snapshot)
   */
  getUserCompanies(): UserCompany[] {
    return this.companiesSubject.value;
  }

  /**
   * Verificar si el usuario tiene acceso a una compañía específica
   */
  canAccessCompany(companyId: number): boolean {
    const companies = this.getUserCompanies();
    return companies.some(c => c.IdCompany === companyId);
  }

  /**
   * Obtener compañía por defecto del usuario
   */
  getDefaultCompany(): UserCompany | null {
    const companies = this.getUserCompanies();
    // Buscar compañía marcada como IsDefault, si no existe retornar la primera
    const defaultCompany = companies.find(c => c.IsDefault === true);
    return defaultCompany || (companies.length > 0 ? companies[0] : null);
  }

  /**
   * Limpiar permisos y compañías (útil para logout)
   */
  clearPermissions(): void {
    this.permissionsSubject.next([]);
    this.companiesSubject.next([]);
    this.userCodeSubject.next(null);
    this.hasNoAccessSubject.next(false);
  }

  /**
   * Verificar si el usuario no tiene accesos (snapshot)
   */
  hasNoAccess(): boolean {
    return this.hasNoAccessSubject.value;
  }

  /**
   * Obtener información del usuario actual
   */
  getCurrentUserInfo(): any {
    const token = localStorage.getItem('id_token');
    if (!token) return null;
    return this.decodeToken(token);
  }
}
